import type { Receipt, ReceiptCategory, TripRoute } from '../types';
import { RECEIPT_CATEGORY_LABELS } from './categoryLabels';

/** Categories where we prefer route leg (origin — dest) when linked from Routes */
const LEG_PREFERRED = new Set<ReceiptCategory>(['flight', 'train', 'bus', 'ferry']);

/**
 * One line per route linked to a receipt (same receipt can have multiple legs).
 */
export function routeLegLabelsByReceiptId(routes: TripRoute[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const rt of routes) {
    if (!rt.source_receipt_id) continue;
    const o = rt.origin.trim();
    const d = rt.destination.trim();
    const leg = o && d ? `${o} — ${d}` : o || d || '';
    if (!leg) continue;
    const prev = m.get(rt.source_receipt_id);
    m.set(rt.source_receipt_id, prev ? `${prev}; ${leg}` : leg);
  }
  return m;
}

/**
 * e.g. `Flight HEL — ARN 590.00 EUR` when a route is linked; otherwise `Food Restaurant Farang 100.00 EUR`.
 */
/** Display amount for summary lines: EUR override, else original amount + currency. */
export function formatReceiptAmountDisplayLine(r: Receipt): string {
  const eurStr = r.amount_eur != null ? String(r.amount_eur).trim() : '';
  if (eurStr !== '') {
    const n = parseFloat(eurStr);
    return `${(Number.isNaN(n) ? 0 : n).toFixed(2)} EUR`;
  }
  const n = parseFloat(r.amount);
  const v = Number.isNaN(n) ? 0 : n;
  return `${v.toFixed(2)} ${r.currency || 'EUR'}`;
}

export function formatReceiptSummaryLine(r: Receipt, legByReceiptId: Map<string, string>): string {
  const label = RECEIPT_CATEGORY_LABELS[r.category];
  const leg = legByReceiptId.get(r.id)?.trim();
  const fileStem = (r.file_name || '').replace(/\.[^.]+$/i, '').trim();
  const merchant = (r.merchant || '').trim() || fileStem || 'Receipt';
  const amt = formatReceiptAmountDisplayLine(r);

  if (leg && LEG_PREFERRED.has(r.category)) {
    return `${label} ${leg} ${amt}`;
  }
  return `${label} ${merchant} ${amt}`;
}

/** Sort: newer date first, then merchant */
export function compareReceiptsForSummary(a: Receipt, b: Receipt): number {
  const da = a.date || '';
  const db = b.date || '';
  if (da !== db) return db.localeCompare(da);
  return (a.merchant || '').localeCompare(b.merchant || '');
}
